import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { marked } from "marked";
import dayjs from "dayjs";

import { MdDragHandle } from "react-icons/md";
import Button from "shared/Button";
import Select from "ManagementTeam/Shared/Select";
import CircleCheckbox from "ManagementTeam/Shared/CircleCheckbox";
import ThumbsUpButton from "ManagementTeam/Shared/ThumbsUpButton";
import ThumbsDownButton from "ManagementTeam/Shared/ThumbsDownButton";
import CompleteIcon from "ManagementTeam/Shared/CompleteIcon";
import PendingVoteButton from "ManagementTeam/Shared/PendingVoteButton";
import ViewPdfButton from "ManagementTeam/Shared/ViewPdfButton";
import TieVoteIcon from "ManagementTeam/Shared/TieVoteIcon";
import Markdown from "../../Shared/Markdown";

import styles from "./AgendaItemDropdown.module.css";
import PinIcon from "ManagementTeam/Shared/PinIcon";
import Menu_Icon_Action_Items from "ManagementTeam/assets/Menu_Icon_Action_Items.svg";
import { toTitleCase } from "shared/@utils/formatString";
import { BlobProvider } from "@react-pdf/renderer";
import AttendancePdf from "../AttendancePdf";
import PDF from "../AttendancePdf/pdf_Icon.svg";
import { useAgenda } from "../useAgenda";
import { convertBlobToBase64 } from "ManagementTeam/utils/blobParse";
/**A list item that shows the agenda items and their information.
 * Clicking on the left side will open up more information about the item.
 * The user can change the status to complete with the check icon on either side.
 * If the item isn’t published a button will show to the right to publish the item.
 * A dropdown also appears to the right with the options of edit, delete, move/duplicate, and action item.  */
export function AgendaItemDropdown({
  id,
  assignee,
  assignedType,
  title,
  description,
  isNew,
  isCompleted,
  isDraft,
  votingStatus,
  pdfName,
  canDownload,
  onClickPdf,
  onEdit,
  onDelete,
  onMove,
  onActionItem,
  onComplete,
  onPublish,
  readOnly = false,
  hasNotes = false,
  isActionItem = false,
  actionItem = null,
  isAttendanceAgenda = false,
  attendanceItem = null,
  filesAttendance = [],
  onClickAttendancePdf,
}) {
  const [selectOption, setSelectOption] = useState("");
  const [showPublishButton, setShowPublishButton] = useState(false);
  const [showPDFAttendance, setShowPDFAttendance] = useState(false);
  const [pdfAttendance, setPDFAttendance] = useState();
  const { postAttendancePDF } = useAgenda();

  useEffect(() => {
    if (id && pdfAttendance && filesAttendance.length === 0) {
      const payload = {
        agendaId: id,
        files: {
          name: "Attendance PDF",
          data: pdfAttendance,
          mimetype: "pdf",
        },
      };
      postAttendancePDF(payload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfAttendance]);
  //Handle the color of the left box
  let leftBoxColor;
  // eslint-disable-next-line default-case
  switch (assignedType) {
    case "Diamond":
      leftBoxColor = "#7F6A45";
      break;
    case "WWG":
      leftBoxColor = "#91B6BB";
      break;
    case "Attorney":
      leftBoxColor = "#243746";
      break;
  }

  const handleDecisionsLink = () => {
    //Put stuff to handle routing to the decisions page when thumbs up/down/tied buttons are clicked
  };

  const handleSelectOptions = (option) => {
    // eslint-disable-next-line default-case
    switch (option) {
      case "Edit":
        onEdit();
        setShowPublishButton(true);
        break;
      case "Delete":
        onDelete(title, description);
        break;
      case "Move/Duplicate":
        onMove(title, description);
        setShowPublishButton(true);
        break;
      case "Action Item":
        onActionItem(title, description);
        setShowPublishButton(true);
        break;
    }
  };

  const dropdownOptions = isActionItem
    ? ["Edit", "Delete"]
    : ["Edit", "Delete", "Move/Duplicate", "Action Item"];
  return (
    <>
      <div className={styles.agendaItemDropdownContainer}>
        {!isActionItem && (
          <MdDragHandle
            style={{ fontSize: "30px" }}
            className={styles.dragger}
          />
        )}
        <div className={styles.agendaItemDropdownBody}>
          <div
            className={styles.leftBox}
            style={{ backgroundColor: leftBoxColor }}
          >
            {isNew && (
              <span className={styles.newBubbleContainer}>
                <span className={styles.newBubble} />
              </span>
            )}
            <CircleCheckbox
              checked={isCompleted}
              onChange={() => (isCompleted = !isCompleted)}
            />
          </div>
          <div className={styles.descriptionBoxContainer}>
            <div className={styles.descriptionBox}>
              <span>{toTitleCase(assignee)}</span>
              <span className={styles.bold}>{title}</span>
              {description && (
                <Markdown content={description ? description : ""} />
              )}
            </div>
            <div className={styles.icons}>
              {actionItem && (
                <div className={styles.actionItemsIcon}>
                  <img src={Menu_Icon_Action_Items} alt={""} />
                </div>
              )}
              {hasNotes && (
                <div className={styles.pinIcon}>
                  <PinIcon
                    onClick={() => {
                      if (!readOnly) {
                        onEdit();
                      }
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          <div className={styles.border}></div>
          <div className={styles.votingResult}>
            {votingStatus === "Passed" ? (
              <>
                <ThumbsUpButton onClick={() => handleDecisionsLink()} />
                <span style={{ color: "#39b54a" }}>Passed!</span>
              </>
            ) : votingStatus === "Failed" ? (
              <>
                <ThumbsDownButton onClick={() => handleDecisionsLink()} />
                <span style={{ color: "#ed1f24" }}>Failed</span>
              </>
            ) : votingStatus === "Complete" ? (
              <>
                <CompleteIcon onClick={() => handleDecisionsLink()} />
                <span style={{ color: "var(--color-alt-text)" }}>Complete</span>
              </>
            ) : votingStatus === "Voting in Progress" ? (
              <>
                <PendingVoteButton onClick={() => handleDecisionsLink()} />
                <span
                  style={{
                    color: "var(--color-alt-text)",
                    wordSpacing: "100vw",
                    textAlign: "center",
                  }}
                >
                  Voting in Progress
                </span>
              </>
            ) : votingStatus === "Upcoming" ? (
              <>
                <PendingVoteButton
                  onClick={() => handleDecisionsLink()}
                  isGrayed
                />
                <span style={{ color: "var(--color-text-darkened)" }}>
                  Upcoming
                </span>
              </>
            ) : votingStatus === "Tie Vote" ? (
              <>
                <TieVoteIcon onClick={() => handleDecisionsLink()} />
                <span style={{ color: "#415363" }}>Tie Vote</span>
              </>
            ) : (
              <></>
            )}
          </div>
          <div className={styles.border}></div>
          <div className={styles.documents}>
            {pdfName && (
              <ViewPdfButton
                pdfName={pdfName}
                showIcon={canDownload}
                onClick={() => onClickPdf(pdfName)}
              />
            )}
            {isAttendanceAgenda &&
              filesAttendance.length === 0 &&
              showPDFAttendance && (
                <BlobProvider
                  document={<AttendancePdf attendanceItem={attendanceItem} />}
                  fileName={`${dayjs().format("MM_DD_YYYY")} Agenda`}
                >
                  {({ blob, url, loading, error }) => {
                    if (blob) {
                      convertBlobToBase64(blob).then((res) => {
                        setPDFAttendance(res);
                      });
                    }
                    return (
                      <div>
                        {loading ? (
                          <div style={{ cursor: "progress" }}>
                            <span>PDF is loading...</span>
                          </div>
                        ) : (
                          <a
                            style={{
                              display: "flex",
                              alignItems: "center",
                              color: "var(--color-alt-text)",
                              filter: "var(--icon-filter)",
                              paddingTop: "10px",
                            }}
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <img src={PDF} alt="pdf icon" />
                            <div
                              style={{
                                paddingLeft: "10px",
                                color: "var(--color-alt-text)",
                                fontSize: "14px",
                              }}
                            >
                              Attendance PDF
                            </div>
                          </a>
                        )}
                      </div>
                    );
                  }}
                </BlobProvider>
              )}
            {isAttendanceAgenda &&
              filesAttendance.length === 0 &&
              !showPDFAttendance && (
                <button
                  className={styles.getAttendanceBtn}
                  onClick={() => setShowPDFAttendance(true)}
                >
                  Get Attendance
                </button>
              )}
            {isAttendanceAgenda && filesAttendance.length > 0 && (
              <ViewPdfButton
                pdfName={filesAttendance[0]?.name}
                onClick={() => {
                  onClickAttendancePdf();
                }}
              />
            )}
          </div>
          <div className={styles.border}></div>
          <div className={styles.complete}>
            <CircleCheckbox
              readOnly={readOnly}
              checked={isCompleted}
              onChange={() => {
                isCompleted = !isCompleted;
                onComplete();
              }}
            />
          </div>
        </div>
        <span
          style={{ visibility: !readOnly ? "visible" : "hidden" }}
          className={styles.selectContainer}
        >
          <Select
            placeholder={selectOption || "Options"}
            options={dropdownOptions}
            onChange={handleSelectOptions}
          />
        </span>
        <span
          style={{
            visibility: !readOnly ? "visible" : "hidden",
            width: "75px",
          }}
        >
          {isDraft && !isActionItem && (
            <Button
              purpose="save"
              children="Publish"
              className={styles.publishButton}
              onClick={() => {
                onPublish();
              }}
            />
          )}
        </span>
      </div>
    </>
  );
}

AgendaItemDropdown.propTypes = {
  /**Assignee of the decision (Bold Text)  */
  assignee: PropTypes.string,
  /**The type of assigned item, either "Diamond”, "WWG”, or "Attorney”.
   * Left Brown Box (#7F6A45) is Diamond, Left Teal Box (#91B6BB) is WWG, and Left Gray Box (#ADB7C0) is Attorney.  */
  assignedType: PropTypes.string,
  /**Title of the decision item (Regular Text)  */
  title: PropTypes.string,
  /**Description of the details of the decision item (Italic Text)  */
  description: PropTypes.string,
  /**Boolean that holds if the item is new to the list (Shows the green notification mark on the top left corner like the Agenda Item example)  */
  isNew: PropTypes.bool,
  /**Boolean that holds if the item is a draft */
  isDraft: PropTypes.bool,
  /**Boolean that holds if the item is completed. A checkmark will show on the left and right if it is.
   * User can click on either check mark to switch between true/false  */
  isCompleted: PropTypes.bool,
  /**The current voting status of the decision item (Passed, Failed, Pending, Upcoming, Complete or Tie vote)  */
  votingStatus: PropTypes.string,
  /**A string that holds the name of the PDF file for the View PDF Button */
  pdfName: PropTypes.string,
  /**Function that’s used to view the PDF when clicking the View PDF Button  */
  onClickPdf: PropTypes.func,
  /**Function called when edit is clicked  */
  onEdit: PropTypes.func,
  /**Function called when delete is clicked  */
  onDelete: PropTypes.func,
  /**Function called when Move/Duplicate is clicked  */
  onMove: PropTypes.func,
  /**Function called when action item is clicked  */
  onActionItem: PropTypes.func,
  /**Function called when a draft item is to be published  */
  onPublish: PropTypes.func,
};
